import { UploadOutlined } from '@ant-design/icons';
import { Alert, Button, message, Modal, ModalProps, Typography, Upload, UploadFile } from 'antd';
import { Helmet } from 'react-helmet-async';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../../api/useAPIMutation';
import useAPIQuery from '../../api/useAPIQuery';
import { ImportFileCollection } from '../../types';
import Form from '../Form';

const mimeTypes = {
  csv: 'text/csv',
};

function fileTypeToMimeType(extension: string): string | undefined {
  return mimeTypes[extension as keyof typeof mimeTypes];
}

const supportedFiles = ['.csv', 'text/csv'];

const maxFileSizeInBytes = 10_485_760; //10Mb

const TITLES: Record<ImportFileCollection['type'], string> = {
  IMPORT_CANDIDATES: 'Importar do Banco de Talentos',
  MATCH_RESULTS: 'Importar Match dos Candidatos',
  TEST_RESULTS: 'Importar Notas dos Candidatos',
};

interface ImportModalFormValues {
  attachment: UploadFile[];
}

interface ImportModalProps extends ModalProps {
  type: ImportFileCollection['type'];
  onSuccess?: (
    data: void,
    variables: ImportModalFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const ImportFileModal = ({ type, onSuccess, okButtonProps, ...props }: ImportModalProps) => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();

  const mutation = useAPIMutation(
    async ({ attachment }: ImportModalFormValues, api) => {
      const formData = new FormData();
      formData.append('file', attachment[0].originFileObj ?? '');
      formData.append('type', type);
      await api.post('/import-files/', formData, {
        maxBodyLength: maxFileSizeInBytes,
      });
    },
    {
      async onSettled() {
        await queryClient.invalidateQueries('/import-files/');
      },
      onSuccess,
    }
  );

  const hasPendingImportFile = useAPIQuery(
    ['/import-files/', { params: { status__in: 'PENDING,IN_PROGRESS', type } }],
    {
      select(data) {
        return data?.count > 0;
      },
    }
  );

  return (
    <Modal
      onOk={() => form.submit()}
      okButtonProps={{
        loading: mutation.isLoading,
        disabled: hasPendingImportFile.data,
        ...okButtonProps,
      }}
      title={<Typography.Text>{TITLES[type]}</Typography.Text>}
      {...props}
    >
      <Helmet>
        <title>{`${TITLES[type]}`} | Recrutamento e Seleção</title>
      </Helmet>
      <Form<ImportModalFormValues>
        form={form}
        layout="vertical"
        initialValues={{}}
        onFinish={(variables) => {
          mutation.mutate(variables);
        }}
      >
        <Form.Item
          label="Arquivo"
          name="attachment"
          valuePropName="fileList"
          getValueFromEvent={(e) => {
            if (Array.isArray(e)) {
              return e;
            }
            return e && e.fileList;
          }}
        >
          <Upload
            maxCount={1}
            listType="text"
            accept={supportedFiles.join(',')}
            beforeUpload={(file: UploadFile) => {
              if (fileTypeToMimeType(file.name.split('.').pop()?.toLowerCase() || '')) {
                if (!file.size) {
                  message.error('Erro com o arquivo.');
                  return Upload.LIST_IGNORE;
                }
                if (file.size >= maxFileSizeInBytes) {
                  message.error('Por favor, selecione um arquivo de até 10Mb no campo Arquivo.');
                  return Upload.LIST_IGNORE;
                }

                return false;
              } else {
                message.error('Por favor, selecione um arquivo no formato ".csv".');
                return Upload.LIST_IGNORE;
              }
            }}
            disabled={hasPendingImportFile.data}
          >
            <Button icon={<UploadOutlined />}>Subir arquivo</Button>
          </Upload>
        </Form.Item>
        {hasPendingImportFile.data && (
          <Alert
            message={
              <Typography.Text>
                <Typography.Text strong>Atenção:</Typography.Text> Há um arquivo em processo de
                importação. Aguarde até que o processo seja finalizado para importar um novo.
              </Typography.Text>
            }
            type="warning"
            showIcon
          />
        )}
      </Form>
    </Modal>
  );
};

export default ImportFileModal;
